import { AxiosRequestConfig } from 'axios';

import { httpClient2 } from 'src/infrastructure/admi-client/http-client';
import {
    CalculateSteckbriefRequest,
    CreateSteckbriefRequest,
    CreateVariantRequest,
    LookalikeComparisonDto,
    LookalikeJobAcceptedDto,
    LookalikeJobResultDto,
    LookalikePackageSelectionDto,
    PreviewSteckbriefDocumentRequest,
    PvEstimateDto,
    RefreshPvEstimateRequest,
    SteckbriefAssumptionDefaultsDto,
    SteckbriefCalculationResultDto,
    SteckbriefDocumentPreview,
    SteckbriefDto,
    SteckbriefKind,
    SteckbriefVariantDto,
    UpdateSteckbriefRequest,
    VariantDocumentDto,
    VariantPrefillDto,
} from './ensan-steckbrief.dto';

/**
 * Per-call request options: cancellation only — the Variant form page aborts
 * its loads on unmount and route switch, and the form the `/calculate` run a
 * newer one supersedes. Deliberately no timeouts: the product is internal and
 * low-traffic, so a slow answer beats a failed one (Cloudflare's 100 s origin
 * limit is the only ceiling).
 */
export interface SteckbriefRequestOptions {
    signal?: AbortSignal;
}

/** The axios config of a call: the caller's signal, nothing else. */
function requestConfig(options: SteckbriefRequestOptions): AxiosRequestConfig {
    return options.signal ? { signal: options.signal } : {};
}

/** Attachment file name of a `Content-Disposition` header, null when unparsable. */
function contentDispositionFileName(header: unknown): string | null {
    if (typeof header !== 'string') return null;
    const match = header.match(/filename="?([^";]+)"?/i);
    return match?.[1] ?? null;
}

export const ensanSteckbriefService = {
    async createSteckbrief(request: CreateSteckbriefRequest): Promise<SteckbriefDto> {
        const response = await httpClient2.post<SteckbriefDto>('/v1/ensan/steckbriefe', request);
        return response.data;
    },

    /**
     * Lists the Steckbriefe of a real estate asset, newest first.
     *
     * `includeVariants` expands each Steckbrief's Variants into `variants` server-side
     * (GEN-760) — the Steckbriefe tab needs them for every row (count column + expanded
     * list) and used to fetch them one request per Steckbrief (Sentry ADMI-FRONTEND-64).
     */
    async getSteckbriefe(realEstateAssetId: string, includeVariants = false): Promise<SteckbriefDto[]> {
        const response = await httpClient2.get<SteckbriefDto[]>('/v1/ensan/steckbriefe', {
            params: { realEstateAssetId, ...(includeVariants ? { include: ['variants'] } : {}) },
        });
        return response.data;
    },

    /**
     * The model defaults per Gebäudetyp (GEN-802) — read-only configuration the
     * Variant form seeds its defaulted fields from (the window area share) and
     * follows on a Gebäudetyp pick. The backend stays authoritative: it applies
     * the same default to a missing value and stamps a matching one as
     * `DEFAULT`.
     */
    async getSteckbriefAssumptionDefaults(): Promise<SteckbriefAssumptionDefaultsDto> {
        const response = await httpClient2.get<SteckbriefAssumptionDefaultsDto>(
            '/v1/ensan/steckbriefe/assumption-defaults',
        );
        return response.data;
    },

    /** Gets one Steckbrief by id (used by the routed Variant form page). */
    async getSteckbrief(id: string, options: SteckbriefRequestOptions = {}): Promise<SteckbriefDto> {
        const response = await httpClient2.get<SteckbriefDto>(`/v1/ensan/steckbriefe/${id}`, requestConfig(options));
        return response.data;
    },

    /**
     * Partially updates a Steckbrief (name and/or building subset). The Variant
     * form page PATCHes `buildingIds` on every map-subset toggle (GEN-765) —
     * the change persists immediately, even when the Variant is never saved,
     * because the prefill resolves server-side from the persisted subset.
     */
    async updateSteckbrief(
        id: string,
        request: UpdateSteckbriefRequest,
        options: SteckbriefRequestOptions = {},
    ): Promise<SteckbriefDto> {
        const response = await httpClient2.patch<SteckbriefDto>(
            `/v1/ensan/steckbriefe/${id}`,
            request,
            requestConfig(options),
        );
        return response.data;
    },

    /** Deletes a Steckbrief incl. its building bindings and Variants (idempotent). */
    async deleteSteckbrief(id: string): Promise<void> {
        await httpClient2.delete<void>(`/v1/ensan/steckbriefe/${id}`);
    },

    /** Prefills the Variant form: catalog-resolved values + previous-Variant carry-over. */
    async prefillVariant(
        steckbriefId: string,
        kind: SteckbriefKind,
        options: SteckbriefRequestOptions = {},
    ): Promise<VariantPrefillDto> {
        const response = await httpClient2.post<VariantPrefillDto>(
            `/v1/ensan/steckbriefe/${steckbriefId}/variants/prefill`,
            { kind },
            requestConfig(options),
        );
        return response.data;
    },

    /** Saves a new immutable Variant; the data stamp is built server-side (ADR-011). */
    async createVariant(steckbriefId: string, request: CreateVariantRequest): Promise<SteckbriefVariantDto> {
        const response = await httpClient2.post<SteckbriefVariantDto>(
            `/v1/ensan/steckbriefe/${steckbriefId}/variants`,
            request,
        );
        return response.data;
    },

    /**
     * Lists a Steckbrief's ACTIVE Variants, newest first. Deliberately re-added
     * after GEN-760 replaced the tab's per-Steckbrief fetches with the
     * `include=variants` list expansion: the routed Variant form page (GEN-765)
     * has no Steckbrief LIST in hand — it needs exactly this one Steckbrief's
     * Variant names for the inline name-uniqueness check.
     */
    async getVariants(steckbriefId: string, options: SteckbriefRequestOptions = {}): Promise<SteckbriefVariantDto[]> {
        const response = await httpClient2.get<SteckbriefVariantDto[]>(
            `/v1/ensan/steckbriefe/${steckbriefId}/variants`,
            requestConfig(options),
        );
        return response.data;
    },

    /** Gets one Variant of a Steckbrief (open mode of the Variant form page, GEN-765). */
    async getVariant(
        steckbriefId: string,
        variantId: string,
        options: SteckbriefRequestOptions = {},
    ): Promise<SteckbriefVariantDto> {
        const response = await httpClient2.get<SteckbriefVariantDto>(
            `/v1/ensan/steckbriefe/${steckbriefId}/variants/${variantId}`,
            requestConfig(options),
        );
        return response.data;
    },

    /**
     * Soft-deletes a Variant (GEN-763) — it disappears from lists, reads and the
     * prefill carry-forward and its name becomes reusable; the row and its stamp
     * survive server-side for audit. Idempotent (repeats return 204 again).
     */
    async deleteVariant(steckbriefId: string, variantId: string): Promise<void> {
        await httpClient2.delete<void>(`/v1/ensan/steckbriefe/${steckbriefId}/variants/${variantId}`);
    },

    /**
     * Generates the Steckbrief PDF for a saved Variant and links it (GEN-698).
     * 409 `VARIANT_ALREADY_GENERATED` when the Variant already has a document.
     */
    async generateVariantDocument(steckbriefId: string, variantId: string): Promise<VariantDocumentDto> {
        const response = await httpClient2.post<VariantDocumentDto>(
            `/v1/ensan/steckbriefe/${steckbriefId}/variants/${variantId}/generate`,
        );
        return response.data;
    },

    /**
     * Renders the Steckbrief PDF statelessly from the live form state (GEN-762)
     * and returns it as a Blob for a browser download — the `/calculate` payload
     * plus the kind to print. NOTHING is persisted: no Drive upload, no
     * document_instance, no Variant — the download is the only copy. Error
     * bodies arrive as ProblemDetail like everywhere else: the shared
     * http-instance interceptor reads Blob error bodies back to JSON.
     */
    async previewSteckbriefDocument(
        steckbriefId: string,
        request: PreviewSteckbriefDocumentRequest,
        options: SteckbriefRequestOptions = {},
    ): Promise<SteckbriefDocumentPreview> {
        const response = await httpClient2.post<Blob>(
            `/v1/ensan/steckbriefe/${steckbriefId}/document-preview`,
            request,
            { responseType: 'blob', ...requestConfig(options) },
        );
        return { blob: response.data, fileName: contentDispositionFileName(response.headers?.['content-disposition']) };
    },

    /** Stateless pipeline re-run for the live form preview — nothing is persisted. */
    async calculateSteckbrief(
        steckbriefId: string,
        request: CalculateSteckbriefRequest,
        options: SteckbriefRequestOptions = {},
    ): Promise<SteckbriefCalculationResultDto> {
        const response = await httpClient2.post<SteckbriefCalculationResultDto>(
            `/v1/ensan/steckbriefe/${steckbriefId}/calculate`,
            request,
            requestConfig(options),
        );
        return response.data;
    },

    /**
     * Enqueues a lookalike search (D4, GEN-700): validates the Steckbrief and starts the LLM search
     * in the background. Returns 202 Accepted with a jobId — the caller polls `getLookalikeJob` until
     * READY or FAILED. kein Treffer surfaces as a READY result with `status: NO_MATCH`.
     */
    async enqueueLookalikes(
        steckbriefId: string,
        request: CalculateSteckbriefRequest,
        options: SteckbriefRequestOptions = {},
    ): Promise<LookalikeJobAcceptedDto> {
        const response = await httpClient2.post<LookalikeJobAcceptedDto>(
            `/v1/ensan/steckbriefe/${steckbriefId}/lookalikes`,
            request,
            requestConfig(options),
        );
        return response.data;
    },

    /** Polls the status of an async lookalike search job. PENDING → keep polling; READY/FAILED → done. */
    async getLookalikeJob(
        steckbriefId: string,
        jobId: string,
        options: SteckbriefRequestOptions = {},
    ): Promise<LookalikeJobResultDto> {
        const response = await httpClient2.get<LookalikeJobResultDto>(
            `/v1/ensan/steckbriefe/${steckbriefId}/lookalikes/${jobId}`,
            requestConfig(options),
        );
        return response.data;
    },

    /**
     * The Maßnahmenpaket picker (D4): the READY job's Gebäudevergleich with other packages compared.
     * Stateless and deterministic — band, €/m² aggregation and fingerprint are recomputed server-side
     * from the figures the search precomputed per package. Works on a fresh search's comparison
     * and on the one the Variant keeps (`lookalikeComparison`) alike — no job involved.
     */
    async selectLookalikePackages(
        steckbriefId: string,
        selection: LookalikePackageSelectionDto,
        options: SteckbriefRequestOptions = {},
    ): Promise<LookalikeComparisonDto> {
        const response = await httpClient2.post<LookalikeComparisonDto>(
            `/v1/ensan/steckbriefe/${steckbriefId}/lookalikes/package-selection`,
            selection,
            requestConfig(options),
        );
        return response.data;
    },

    /**
     * Forces a new PV Calculator computation for the current form values
     * (GEN-799; also re-fetches the roof potential) — the advisor's retry after
     * a retryable failure. SYNCHRONOUS: 200 with the stored estimate, READY or
     * FAILED (DISABLED when the feature flag is off, without computing). May
     * take tens of seconds — the server waits for Google Solar / admi Solar and
     * the calculator.
     */
    async refreshPvEstimate(steckbriefId: string, values: Record<string, unknown>): Promise<PvEstimateDto> {
        const request: RefreshPvEstimateRequest = { values };
        const response = await httpClient2.post<PvEstimateDto>(
            `/v1/ensan/steckbriefe/${steckbriefId}/pv-estimate/refresh`,
            request,
        );
        return response.data;
    },
};
